import React from 'react';
import styled from 'styled-components';
import { logNetRequest } from '@utils/usefulFuncs';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { Body5, Container } from '@ui';
import { convertHexToRGBA } from '@utils/usefulFuncs';
import { Working } from '@ui/Working';
import { SelectableDocument } from '../DocumentSelection';

const Wrapper = styled.div`
  padding-top: 24px;
`;
const SearchInputWrapper = styled.div`
  input {
    outline: none;
    border: none;
    font-size: 14px;
    line-height: 18px;

    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    margin: 0;
    padding: 8px 16px;
    width: 100%;
    border-radius: 5px;
    max-width: 600px;

    &::placeholder {
      color: ${({ theme }) => convertHexToRGBA(theme.themeColors.text, 0.6)};
    }

    &:focus {
      outline: none;
    }
  }
`;
const DocumentSelectionWrapper = styled.div`
  padding: 36px 0 0 0;
`;

const isSelected = (doc, selectedDocs) => {
  return selectedDocs.some((d) => d.id === doc.id);
};

const Results = ({ docs, onSelect, onRemoveSelect, selectedDocs }) => {
  return (
    <div>
      {docs.map((doc) => (
        <SelectableDocument
          key={doc.id}
          data={doc}
          onSelect={onSelect}
          onRemoveSelect={onRemoveSelect}
          isSelected={isSelected(doc, selectedDocs)}
        />
      ))}
    </div>
  );
};

export const DocumentSearchSelection = ({ selectedDocs, onSelect, onRemoveSelect }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [results, setResults] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  React.useEffect(() => {
    let cancelled = false;

    const fetchResults = async () => {
      if (!debouncedQuery) {
        return;
      }
      setIsLoading(true);
      setResults(null);
      const results = await onSearch(debouncedQuery);
      if (!cancelled && results) {
        const maxResults = results.slice(0, 30);
        setResults(maxResults);
      }
      if (!cancelled) {
        setIsLoading(false);
      }
    };

    fetchResults();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const onSearch = async (query) => {
    const URL = `v1/genai/documents?category=company&title_query_string=${query}`;
    const { status, data, error } = await sendApiRequest('get', URL);
    handle400Statuses(status);
    logNetRequest(status, data);
    if (status === 200) {
      return data.documents;
    }
    return null;
  };

  const onSearchQueryChange = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <Wrapper>
      <Container bottom={16}>
        <Body5 bold>Document Name Search</Body5>
      </Container>
      <SearchInputWrapper>
        <input value={searchQuery} onChange={onSearchQueryChange} placeholder="Search for documents..." />
      </SearchInputWrapper>

      <DocumentSelectionWrapper>
        {isLoading && (
          <Container top={12} left={16}>
            <Working noVerticalPadding />
          </Container>
        )}

        {results?.length > 0 && (
          <Container top={4} left={8}>
            <Body5 bold>Search Results</Body5>

            <Container top={16}>
              <Results docs={results} onSelect={onSelect} onRemoveSelect={onRemoveSelect} selectedDocs={selectedDocs} />
            </Container>
          </Container>
        )}
        {!isLoading && results?.length === 0 && (
          <Container top={16}>
            <Body5>No results found</Body5>
          </Container>
        )}
      </DocumentSelectionWrapper>
    </Wrapper>
  );
};
